'use client';

import React, { memo, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';

import { Spinner } from '@repo/ui/components/spinner';
import { Button } from '@repo/ui/components/ui/button';
import { Command, CommandInput } from '@repo/ui/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@repo/ui/components/ui/popover';
import { Skeleton } from '@repo/ui/components/ui/skeleton';
import { cn } from '@repo/ui/lib/utils';

import { HOME_TAB } from '@/@types/enums';
import { getBrandCredibilityColor } from '@/lib/metric-utils';
import { BrandListItem } from '@/hooks/api/brand/model';
import { useAppTabs } from '@/providers/app-tabs-provider';

import { AppNavigatorList } from './app-navigator-list';
import { AppNavigatorLogo } from './app-navigator-logo';
import {
  useAppNavigatorBrands,
  useAppNavigatorChannels,
} from './hooks/use-app-navigator';

/**
 * Static brand identifiers - centralized configuration
 */
const STATIC_BRAND_IDS = {
  HOME: 1.1,
  ALL_SOURCES: 1.2,
  HACKER_NEWS: 1.3,
  YOUTUBE: 1.4,
  NEWS: 1.5,
} as const;

/**
 * Predefined brand items for navigation
 */
export const HOME_BRAND_ITEM: BrandListItem = {
  id: STATIC_BRAND_IDS.HOME,
  brand: 'Home',
  logoCircleUrl: '/images/credible-circle-logo.png',
  metrics: null,
  createdAt: '',
  updatedAt: '',
  description: '',
} as BrandListItem;

export const BRANDS_BRAND_ITEM: BrandListItem = {
  id: STATIC_BRAND_IDS.ALL_SOURCES,
  brand: 'All Sources',
  logoCircleUrl: '/images/credible-circle-logo.png',
  metrics: null,
  createdAt: '',
  updatedAt: '',
  description: '',
} as BrandListItem;

export const HACKER_NEWS_BRAND_ITEM: BrandListItem = {
  id: STATIC_BRAND_IDS.HACKER_NEWS,
  brand: 'Hacker News',
  logoCircleUrl: '/images/brand-logos/circle-logo-hackernews.svg',
  logoLargeUrl: '/images/brand-logos/logo-hackernews.png',
  metrics: null,
  createdAt: '',
  updatedAt: '',
  description: '',
} as BrandListItem;

export const YOUTUBE_BRAND_ITEM: BrandListItem = {
  id: STATIC_BRAND_IDS.YOUTUBE,
  brand: 'All Channels',
  logoCircleUrl: '/images/credible-circle-logo.png',
  metrics: null,
  createdAt: '',
  updatedAt: '',
  description: '',
} as BrandListItem;

export const NEWS_BRAND_ITEM: BrandListItem = {
  id: STATIC_BRAND_IDS.NEWS,
  brand: 'All Channels',
  logoCircleUrl: '/images/credible-circle-logo.png',
  metrics: null,
  createdAt: '',
  updatedAt: '',
  description: '',
} as BrandListItem;

/**
 * Checks if a brand ID corresponds to a static/predefined brand
 */
export const isStaticBrand = (brandId: number): boolean => {
  return Object.values(STATIC_BRAND_IDS).includes(
    brandId as (typeof STATIC_BRAND_IDS)[keyof typeof STATIC_BRAND_IDS],
  );
};

/**
 * Additional navigation item configuration
 */
export type AdditionalItemType = {
  value: BrandListItem;
  label: string;
  onClick?: (value: BrandListItem) => void;
};

/**
 * AppNavigator component props
 */
interface AppNavigatorProps {
  additionalItems?: AdditionalItemType[];
  brand?: BrandListItem;
  setBrand?: (brand: BrandListItem) => void;
  className?: string;
  isLoading?: boolean;
  bypassSelectValidation?: boolean;
}

/**
 * Hook to determine the current navigator mode based on homeTab
 */
const useNavigatorMode = (homeTab: HOME_TAB) => {
  return useMemo(() => {
    return homeTab === HOME_TAB.YOUTUBE ? 'channels' : 'brands';
  }, [homeTab]);
};

const PureAppNavigator: React.FC<AppNavigatorProps> = ({
  brand,
  setBrand,
  className,
  additionalItems,
  isLoading,
  bypassSelectValidation,
}) => {
  const { homeTab } = useAppTabs();
  const [open, setOpen] = useState(false);

  // Determine current mode and effective brand
  const mode = useNavigatorMode(homeTab);

  // Only fetch data for the active mode to improve performance
  const shouldFetchBrands = mode === 'brands';
  const shouldFetchChannels = mode === 'channels';

  const brandsHook = useAppNavigatorBrands({
    open: shouldFetchBrands ? open : false,
    setOpen,
    brand,
    setBrand,
    bypassSelectValidation,
  });

  const channelsHook = useAppNavigatorChannels({
    open: shouldFetchChannels ? open : false,
    setOpen,
    brand,
    setBrand,
    bypassSelectValidation,
  });

  // Select the appropriate hook based on mode
  const {
    internSearch,
    handleSearchChange,
    selectBrand,
    brands: data,
    meta,
    isFetching,
    isFetchingNextPage,
    fetchMoreOnScroll,
  } = mode === 'brands'
    ? {
        ...brandsHook,
        brands: brandsHook.brands,
      }
    : {
        ...channelsHook,
        brands: channelsHook.channels,
      };

  // UI text and states
  const buttonTitle = brand
    ? 'Change selected publisher'
    : isLoading
      ? 'Loading...'
      : 'Select publisher...';

  const buttonText =
    brand?.brand || (isLoading ? 'Loading...' : 'Select publisher...');

  const searchPlaceholder =
    mode === 'brands' ? 'Search brand...' : 'Search channel...';

  const percentColor = useMemo(
    () => getBrandCredibilityColor(brand?.metrics?.averageCredibility),
    [brand?.metrics?.averageCredibility],
  );

  return (
    <div
      className={cn(
        'bg-[#F0F2F5] p-3 cred-md:p-3.5 cred-lg:p-4 cred-xl:p-5',
        className,
      )}
    >
      <Popover modal open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            title={buttonTitle}
            className="w-full justify-between border-black bg-white px-1.5 text-black hover:bg-[#0A2342]/80 aria-expanded:bg-[#0A2342] aria-expanded:text-white cred-md:h-auto cred-md:p-[7px] cred-lg:p-[7.6px] cred-xl:rounded-[10px] cred-xl:p-2.5 cred-md:[&_svg]:size-5 cred-lg:[&_svg]:size-5 cred-xl:[&_svg]:size-6"
          >
            <div className="flex items-center gap-2">
              {!isLoading &&
                brand &&
                !isStaticBrand(brand.id) &&
                homeTab === HOME_TAB.NEWS && (
                  <div
                    style={{ backgroundColor: percentColor }}
                    className="flex rounded-sm px-1 py-[3px] shadow-[inset_1px_2px_4px_rgba(62,62,62,0.25)] transition-opacity duration-300"
                  >
                    <span className="block w-10 text-center font-satoshi text-[15px] font-bold leading-4 tracking-wide text-white">
                      {Math.round(brand?.metrics?.averageCredibility ?? 0)}%
                    </span>
                  </div>
                )}
              <div className="flex items-center gap-3">
                {isLoading ? (
                  <Skeleton className="size-6 rounded-full bg-[#4A5568] cred-xl:size-10" />
                ) : brand ? (
                  <AppNavigatorLogo
                    brandId={brand.id}
                    brand={brand.brand}
                    logoCircleUrl={brand.logoCircleUrl}
                  />
                ) : (
                  <div className="size-6 overflow-hidden rounded-full bg-neutral-1/40 cred-md:size-7 cred-xl:size-10" />
                )}
                <span className="text-base font-bold leading-6 cred-md:text-lg cred-md:leading-7 cred-lg:text-base cred-lg:leading-7 cred-xl:text-2xl cred-xl:leading-10">
                  {buttonText}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-center gap-[5px] cred-md:gap-2 cred-lg:gap-2.5 cred-xl:gap-3.5">
              {isLoading ? (
                <Spinner size={20} />
              ) : (
                <ChevronDown className="opacity-80" />
              )}
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          autoFocus={false}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="p-0 w-(--radix-popover-trigger-width)"
        >
          <Command
            shouldFilter={false}
            className="bg-white text-black"
            autoFocus={false}
          >
            <div className="px-[5px] py-[5px] cred-md:px-1.5 cred-md:py-1.5 cred-xl:px-2 cred-xl:py-2">
              <CommandInput
                placeholder={searchPlaceholder}
                autoFocus={false}
                value={internSearch}
                onValueChange={handleSearchChange}
                className="h-auto py-[5px] leading-[20px] placeholder:text-sm placeholder:text-[#757575] cred-md:py-2 cred-md:text-base cred-md:leading-[20px] cred-md:placeholder:text-base cred-md:placeholder:leading-[20px] cred-lg:py-2 cred-lg:text-base cred-lg:leading-[20px] cred-lg:placeholder:text-base cred-lg:placeholder:leading-[20px] cred-xl:py-2.5 cred-xl:text-base cred-xl:leading-[20px] cred-xl:placeholder:text-base cred-xl:placeholder:leading-[20px]"
                wrapperClassName="border rounded-md px-2"
              />
            </div>
            <AppNavigatorList
              additionalItems={additionalItems || []}
              brand={brand}
              selectBrand={selectBrand}
              brands={data}
              meta={meta}
              isFetching={isFetching}
              isFetchingNextPage={isFetchingNextPage}
              fetchMoreOnScroll={fetchMoreOnScroll}
            />
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const AppNavigator = memo(PureAppNavigator);
